import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'
import { refreshBillingAndWait } from '@renderer/chat/store/global-billing'

export interface BillingRefreshNoticeProps {
  /** Fires once billing state is current again, so the surface can resume or dismiss. */
  onRefreshed?: () => void
  className?: string
}

/**
 * Shown when a plan change committed but the billing state behind it did not
 * publish. The plan already changed server-side, so this only ever re-reads. It
 * must never offer to run the purchase again.
 *
 * Onboarding renders this too and mounts no PylonProvider, so a support button
 * would silently do nothing there.
 */
export function BillingRefreshNotice({ onRefreshed, className }: BillingRefreshNoticeProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [retryFailed, setRetryFailed] = useState(false)

  const refresh = async () => {
    setIsRefreshing(true)
    setRetryFailed(false)
    try {
      if (await refreshBillingAndWait()) {
        onRefreshed?.()
        return
      }
      setRetryFailed(true)
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <div className={twMerge('flex flex-col items-center gap-3 p-6 text-center', className)}>
      <p className="text-foreground-secondary m-0 text-sm">
        Your plan change was saved, but billing details could not be refreshed.
      </p>
      <Button variant="outline" loading={isRefreshing} onClick={refresh}>
        Refresh billing details
      </Button>
      {/* Mounted empty rather than conditionally rendered: a live region that
          appears with its text already in it is not announced. */}
      <p role="status" aria-live="polite" className="text-foreground-secondary m-0 min-h-4 text-xs">
        {retryFailed ? 'Still no answer from billing. Your plan change is safe, so don’t buy again.' : ''}
      </p>
    </div>
  )
}
